import { useState, useEffect, useCallback } from "react";
import { sendRegistrationEmail } from "../../context/RegistrationEmail";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const COURSES_API_URL = process.env.REACT_APP_API_URL;

const STEPS = [
  {
    label: "Tell us about you",
    detail: "A few details so we can reach you the right way.",
  },
  {
    label: "Tell us your goal",
    detail: "Course, mode, and timeline — so we can match you right.",
  },
  {
    label: "We take it from there",
    detail: "Our team calls you back to confirm the next steps.",
  },
];

const INTERESTED_FOR_OPTIONS = ["online class", "offline class"];

const HOW_SOON_OPTIONS = [
  "immediate",
  "within a week",
  "within 15 days",
  "other",
];

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  whatsappNumber: "",
  address: "",
  highestQualification: "",
  course: "",
  interestedFor: "",
  howSoon: "",
  message: "",
};

const inputClasses =
  "w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-400/25";

export default function DemoClassRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
}) {
  const [form, setForm] = useState(initialFormState);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successData, setSuccessData] = useState(null);

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState("");

  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingError, setUpcomingError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setForm(initialFormState);
      setFieldErrors({});
      setServerError("");
      setSuccessData(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchCourses = async () => {
      setCoursesLoading(true);
      setCoursesError("");

      try {
        const response = await fetch(`${COURSES_API_URL}/courses/get-all`);
        const result = await response.json();

        if (cancelled) return;

        const list = Array.isArray(result)
          ? result
          : result.data || result.courses || [];

        setCourses(list);
      } catch (error) {
        console.error("Error fetching courses:", error);

        if (!cancelled) {
          setCourses([]);
          setCoursesError("Couldn't load courses. Please retry.");
        }
      } finally {
        if (!cancelled) {
          setCoursesLoading(false);
        }
      }
    };

    fetchCourses();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchUpcomingCourses = async () => {
      setUpcomingLoading(true);
      setUpcomingError("");

      try {
        const response = await fetch(
          `${COURSES_API_URL}/upcoming-courses/active`
        );

        const result = await response.json();

        if (cancelled) return;

        if (result.success) {
          setUpcomingCourses(result.data || []);
        } else {
          setUpcomingCourses([]);
          setUpcomingError(
            result.message || "Couldn't load upcoming courses."
          );
        }
      } catch (error) {
        console.error("Error fetching upcoming courses:", error);

        if (!cancelled) {
          setUpcomingCourses([]);
          setUpcomingError(
            "Couldn't load upcoming courses. Please retry."
          );
        }
      } finally {
        if (!cancelled) {
          setUpcomingLoading(false);
        }
      }
    };

    fetchUpcomingCourses();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const updateField = (field) => (e) => {
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const validate = () => {
    const errors = {};

    if (!form.name.trim() || form.name.trim().length < 2) {
      errors.name = "Enter the full name.";
    }

    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      errors.email = "Enter a valid email address.";
    }

    if (!/^[0-9]{10}$/.test(form.phone.trim())) {
      errors.phone = "Enter a valid 10-digit phone number.";
    }

    if (!/^[0-9]{10}$/.test(form.whatsappNumber.trim())) {
      errors.whatsappNumber =
        "Enter a valid 10-digit WhatsApp number.";
    }

    if (!form.address.trim()) {
      errors.address = "Enter your address.";
    }

    if (!form.highestQualification.trim()) {
      errors.highestQualification =
        "Enter your highest qualification.";
    }

    if (!form.course) {
      errors.course = "Select a course.";
    }

    if (!form.interestedFor) {
      errors.interestedFor = "Select online or offline.";
    }

    if (!form.howSoon) {
      errors.howSoon = "Let us know how soon.";
    }

    return errors;
  };

  const getSelectedCourseName = () => {
    if (!form.course) return "";

    if (form.course.startsWith("upcoming:")) {
      const courseId = form.course.replace("upcoming:", "");

      const selectedCourse = upcomingCourses.find(
        (course) => course._id === courseId
      );

      return (
        selectedCourse?.name ||
        selectedCourse?.title ||
        selectedCourse?.category ||
        form.course
      );
    }

    const selectedCourse = courses.find(
      (course) => course._id === form.course
    );

    return (
      selectedCourse?.category ||
      selectedCourse?.name ||
      selectedCourse?.title ||
      form.course
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setServerError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/demoClass/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        if (result.errors?.length) {
          setServerError(result.errors.join(" "));
        } else {
          setServerError(
            result.message ||
              "Registration failed. Please try again."
          );
        }

        return;
      }

      const emailFormData = {
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        whatsapp: form.whatsappNumber,
        address: form.address,
        qualification: form.highestQualification,
        course: getSelectedCourseName(),
        mode: form.interestedFor,
        timeline: form.howSoon,
        questions: form.message,
      };

      try {
        await sendRegistrationEmail(emailFormData);
        console.log("Registration email sent successfully.");
         setSuccessData(result.data);
      onSuccess?.(result.data);
      } catch (emailError) {
        console.error(
          "Registration succeeded but email could not be sent:",
          emailError
        );

        setServerError(
          "Registration was successful, but the notification email could not be sent. Please contact the administrator."
        );
      }

     
    } catch (err) {
      console.error("Registration error:", err);

      setServerError(
        "Could not reach the server. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#0F1121]/60 p-5 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(e) =>
        e.target === e.currentTarget && onClose?.()
      }
    >
      <div
        className="relative max-h-[92vh] w-full max-w-[880px] overflow-y-auto rounded-[20px] bg-[#FDFBF7] font-sans shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
      >
        <button
          type="button"
          aria-label="Close registration form"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#1B1F3B]/[0.06] text-xl leading-none text-[#1B1F3B] transition hover:opacity-90 active:scale-95"
          onClick={() => onClose?.()}
        >
          &times;
        </button>

        <div className="flex flex-wrap">
          <aside className="min-w-[260px] flex-1 basis-[280px] rounded-t-[20px] bg-[#1B1F3B] p-8 text-[#FDFBF7] sm:rounded-l-[20px] sm:rounded-tr-none">
            <p className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-400">
              Free &middot; No obligation
            </p>

            <h2
              id="demo-modal-title"
              className="mb-3 text-[28px] font-bold leading-[1.15] tracking-tight"
            >
              Tell us your goal.
              <br />
              We&rsquo;ll take it from there.
            </h2>

            <p className="mb-7 text-sm leading-relaxed text-[#FDFBF7]/70">
              Share a few details and our team will reach out to plan
              your next step.
            </p>

            <ol className="flex flex-col gap-5">
              {STEPS.map((step, i) => (
                <li
                  key={step.label}
                  className="flex items-start gap-3.5"
                >
                  <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-full bg-amber-400 text-xs font-bold text-[#1B1F3B]">
                    {i + 1}
                  </span>

                  <div>
                    <p className="mb-1 text-[13px] font-bold text-[#FDFBF7]">
                      {step.label}
                    </p>

                    <p className="text-[12.5px] leading-relaxed text-[#FDFBF7]/65">
                      {step.detail}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </aside>

          <div className="min-w-[300px] flex-[2] basis-[420px] p-9">
            {successData ? (
              <div className="flex flex-col items-start gap-2.5 py-5">
                <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-400 text-xl text-white">
                  &#10003;
                </div>

                <h3 className="text-xl font-bold text-[#1B1F3B]">
                  You&rsquo;re registered
                </h3>

                <p className="mb-3 text-sm leading-relaxed text-slate-500">
                  {successData.name}, thanks for registering for{" "}
                  <strong>
                    {successData.course?.name ||
                      successData.course?.title}
                  </strong>
                  . A confirmation is on its way to{" "}
                  <strong>{successData.email}</strong>.
                </p>

                <button
                  type="button"
                  className="mt-1 rounded-xl bg-[#1B1F3B] px-5 py-3 text-sm font-bold text-[#FDFBF7] transition hover:opacity-90 active:scale-[0.98]"
                  onClick={() => onClose?.()}
                >
                  Done
                </button>
              </div>
            ) : (
              <form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit}
                noValidate
              >
                <h3 className="mb-1 text-xl font-bold text-[#1B1F3B]">
                  Register your interest
                </h3>

                <div className="flex flex-wrap gap-3.5">
                  <Field
                    label="Full name"
                    error={fieldErrors.name}
                  >
                    <input
                      type="text"
                      value={form.name}
                      onChange={updateField("name")}
                      placeholder="Asha Rao"
                      className={inputClasses}
                    />
                  </Field>

                  <Field
                    label="Email"
                    error={fieldErrors.email}
                  >
                    <input
                      type="email"
                      value={form.email}
                      onChange={updateField("email")}
                      placeholder="asha@example.com"
                      className={inputClasses}
                    />
                  </Field>
                </div>

                <div className="flex flex-wrap gap-3.5">
                  <Field
                    label="Phone number"
                    error={fieldErrors.phone}
                  >
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={updateField("phone")}
                      placeholder="9876543210"
                      className={inputClasses}
                    />
                  </Field>

                  <Field
                    label="WhatsApp number"
                    error={fieldErrors.whatsappNumber}
                  >
                    <input
                      type="tel"
                      value={form.whatsappNumber}
                      onChange={updateField("whatsappNumber")}
                      placeholder="9876543210"
                      className={inputClasses}
                    />
                  </Field>
                </div>

                <div className="flex flex-wrap gap-3.5">
                  <Field
                    label="Address"
                    error={fieldErrors.address}
                  >
                    <input
                      type="text"
                      value={form.address}
                      onChange={updateField("address")}
                      placeholder="House no, street, city, state"
                      className={inputClasses}
                    />
                  </Field>
                </div>

                <div className="flex flex-wrap gap-3.5">
                  <Field
                    label="Highest qualification"
                    error={fieldErrors.highestQualification}
                  >
                    <input
                      type="text"
                      value={form.highestQualification}
                      onChange={updateField(
                        "highestQualification"
                      )}
                      placeholder="e.g. B.Tech, 12th grade"
                      className={inputClasses}
                    />
                  </Field>

                  <Field
                    label="Course"
                    error={
                      fieldErrors.course ||
                      coursesError ||
                      upcomingError
                    }
                  >
                    <select
                      value={form.course}
                      onChange={updateField("course")}
                      className={inputClasses}
                      disabled={
                        coursesLoading ||
                        (!!coursesError &&
                          upcomingCourses.length === 0)
                      }
                    >
                      <option value="">
                        {coursesLoading || upcomingLoading
                          ? "Loading courses…"
                          : "Select course"}
                      </option>

                      {courses.map((c) => (
                        <option
                          key={`course-${c._id}`}
                          value={c._id}
                        >
                          {c.category}
                        </option>
                      ))}

                      {upcomingCourses.map((c) => (
                        <option
                          key={`upcoming-${c._id}`}
                          value={`upcoming:${c._id}`}
                        >
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="flex flex-wrap gap-3.5">
                  <Field
                    label="Interested for"
                    error={fieldErrors.interestedFor}
                  >
                    <select
                      value={form.interestedFor}
                      onChange={updateField("interestedFor")}
                      className={inputClasses}
                    >
                      <option value="">Select mode</option>

                      {INTERESTED_FOR_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    label="How soon are you planning your career?"
                    error={fieldErrors.howSoon}
                  >
                    <select
                      value={form.howSoon}
                      onChange={updateField("howSoon")}
                      className={inputClasses}
                    >
                      <option value="">Select timeline</option>

                      {HOW_SOON_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="flex flex-wrap gap-3.5">
                  <Field label="Any other questions? (optional)">
                    <textarea
                      value={form.message}
                      onChange={updateField("message")}
                      placeholder="Anything else you'd like us to know..."
                      rows={3}
                      className={`${inputClasses} resize-y`}
                    />
                  </Field>
                </div>

                {serverError && (
                  <p className="rounded-lg bg-orange-600/10 px-3 py-2.5 text-sm text-orange-600">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 rounded-xl bg-[#1B1F3B] px-5 py-3.5 text-sm font-bold text-[#FDFBF7] transition hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Register"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="flex flex-1 basis-[200px] flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-slate-500">
        {label}
      </span>

      {children}

      {error && (
        <span className="text-[11.5px] text-orange-600">
          {error}
        </span>
      )}
    </label>
  );
}